/* Gruppen, Sichtbarkeit und Ebenen: reine Funktionen. Gruppen sind benannte Mengen aus Brush- und Entity-IDs. */
import type { Doc, Entity, Sel } from './types.ts';
import Core from '../core.ts';
import EntDefs from './entdefs.ts';
import I18n from '../i18n.ts';
/** Gruppe: benannte Menge aus Brush- und Punkt-Entity-IDs; sticky = Auswahl zieht die ganze Gruppe mit */
export interface Group { id: string; name: string; hidden: boolean; sticky: boolean; members: Set<string> }
/** Sichtbarkeit: einzeln versteckte IDs, versteckte automatische Ebenen und Gruppen */
export interface VisState { manual: Set<string>; auto: Set<string>; groups: Group[] }
type Pos = [number, number];
/** Speicherbare Form (Kommentar am Ende der .map) */
export interface SavedGroups {
  v: number;
  groups: { id?: string; name?: string; hidden?: boolean; sticky?: boolean; m?: string[]; p?: Pos[] }[];
  manual?: string[]; manualP?: Pos[]; auto?: string[];
}

const Groups = (function (Core, EntDefs) {
  'use strict';
  const _ = I18n.t;
  const AUTO: [string, string][] = [['world', 'grp.world'], ['func', 'pn.g.func'], ['trigger', 'pn.g.trigger'], ['light', 'pn.g.light'], ['monster', 'pn.g.monster'], ['item', 'pn.g.item'], ['player', 'grp.player'], ['sound', 'pn.sounds'], ['other', 'grp.other']];
  const isPoint = (e: Entity, i: number): boolean => i > 0 && !e.brushes.length;
  /** Automatische Ebene eines Objekts: Welt oder Klassengruppe */
  function autoKey(ent: Entity, i: number): string {
    if (i === 0) return 'world';
    const c = ent.kv.classname || '';
    if (EntDefs.iconKind(c)) return 'sound';
    return EntDefs.get(c).g || 'other';
  }
  /** Menge der versteckten IDs (Brushes und Punkt-Entities).
   *  st = { manual:Set, auto:Set(Schlüssel), groups:[{hidden, members:Set}] } */
  function hiddenIds(doc: Doc, st: VisState): Set<string> {
    const out = new Set(st.manual);
    (st.groups || []).forEach((g) => { if (g.hidden) g.members.forEach((id) => out.add(id)); });
    if (st.auto && st.auto.size) doc.ents.forEach((e, i) => {
      if (!st.auto.has(autoKey(e, i))) return;
      if (isPoint(e, i)) out.add(e.id); else e.brushes.forEach((b) => out.add(b.id));
    });
    return out;
  }
  /** Dokument ohne versteckte Objekte (die Welt bleibt als Eintrag 0 erhalten) */
  function filterDoc(doc: Doc, hidden?: Set<string> | null): Doc {
    if (!hidden || !hidden.size) return doc;
    const ents: Entity[] = [];
    doc.ents.forEach((e, i) => {
      if (i === 0) { ents.push(Object.assign({}, e, { brushes: e.brushes.filter((b) => !hidden.has(b.id)) })); return; }
      if (isPoint(e, i)) { if (!hidden.has(e.id)) ents.push(e); return; }
      const bs = e.brushes.filter((b) => !hidden.has(b.id));
      if (bs.length) ents.push(bs.length === e.brushes.length ? e : Object.assign({}, e, { brushes: bs }));
    });
    return Object.assign({}, doc, { ents: ents });
  }
  /** Zählt je automatischer Ebene die Objekte */
  function autoCounts(doc: Doc): Record<string, number> {
    const c: Record<string, number> = {}; AUTO.forEach((a) => { c[a[0]] = 0; });
    doc.ents.forEach((e, i) => { c[autoKey(e, i)] += isPoint(e, i) ? 1 : e.brushes.length; });
    return c;
  }
  /** IDs, die es im Dokument gibt (Brushes + Punkt-Entities) */
  function liveIds(doc: Doc): Set<string> {
    const s = new Set<string>(); doc.ents.forEach((e, i) => { e.brushes.forEach((b) => s.add(b.id)); if (isPoint(e, i)) s.add(e.id); }); return s;
  }
  function selIds(sel: Sel): Set<string> { const s = new Set<string>(); sel.b.forEach((id) => s.add(id)); sel.e.forEach((id) => s.add(id)); return s; }
  /** Auswahl auf ganze Gruppen erweitern: neu Hinzugekommenes zieht seine (klebende) Gruppe mit, Entferntes ebenso */
  function expand(groups: Group[], oldSel: Sel, newSel: Sel, isPt?: (id: string) => boolean): Sel {
    const sticky = groups.filter((g) => g.sticky);
    if (!sticky.length) return newSel;
    const o = selIds(oldSel), n = selIds(newSel), out = { b: new Set(newSel.b), e: new Set(newSel.e) };
    const addId = (id: string) => { out.b.add(id); };
    sticky.forEach((g) => {
      let added = false, removed = false;
      g.members.forEach((id) => { if (n.has(id) && !o.has(id)) added = true; if (o.has(id) && !n.has(id)) removed = true; });
      if (added && !removed) g.members.forEach((id) => { if (isPt && isPt(id)) out.e.add(id); else addId(id); });
      else if (removed && !added) g.members.forEach((id) => { out.b.delete(id); out.e.delete(id); });
    });
    return out;
  }
  /** Mitglieder nach Art trennen: welche IDs sind Punkt-Entities */
  function splitIds(doc: Doc, ids: Set<string>): Sel {
    const b = new Set<string>(), e = new Set<string>();
    doc.ents.forEach((en, i) => { if (isPoint(en, i)) { if (ids.has(en.id)) e.add(en.id); } else en.brushes.forEach((br) => { if (ids.has(br.id)) b.add(br.id); }); });
    return { b: b, e: e };
  }
  /** Speicherbare Form (v2): Mitglieder als stabile IDs (m), zusätzlich als Positionen (p = [Entity-Index, Brush-Index])
   *  als Rückfall für Dateien, die ein anderes Programm ohne unsere ID-Kommentare neu geschrieben hat. */
  function serialize(doc: Doc, st: VisState): SavedGroups & { groups: NonNullable<SavedGroups['groups']>; manual: string[]; auto: string[] } {
    const pos = new Map<string, Pos>();
    doc.ents.forEach((e, i) => { e.brushes.forEach((b, j) => pos.set(b.id, [i, j])); if (isPoint(e, i)) pos.set(e.id, [i, -1]); });
    const ids = (set: Set<string>): string[] => { const r: string[] = []; set.forEach((id) => { if (pos.has(id)) r.push(id); }); return r; };
    const ps = (set: Set<string>): Pos[] => ids(set).map((id) => pos.get(id) as Pos);
    return {
      v: 2,
      groups: (st.groups || []).map((g) => ({ id: g.id, name: g.name, hidden: !!g.hidden, sticky: !!g.sticky, m: ids(g.members), p: ps(g.members) })),
      manual: ids(st.manual), manualP: ps(st.manual), auto: Array.from(st.auto || [])
    };
  }
  /** Stellt Gruppen/Sichtbarkeit wieder her. IDs haben Vorrang; findet keine einzige ID ein Objekt, gelten die Positionen. */
  function restore(doc: Doc, data: SavedGroups | null | undefined, newId: () => string): VisState {
    const st: VisState = { groups: [], manual: new Set<string>(), auto: new Set<string>() };
    if (!data || (data.v !== 1 && data.v !== 2)) return st;
    const live = liveIds(doc);
    const idAt = ([i, j]: Pos): string | null => { const e = doc.ents[i]; if (!e) return null; if (j < 0) return e.id; const b = e.brushes[j]; return b ? b.id : null; };
    const fromPos = (list?: Pos[]): Set<string> => { const s = new Set<string>(); (list || []).forEach((p) => { const id = idAt(p); if (id) s.add(id); }); return s; };
    const fromIds = (list?: string[]): Set<string> => { const s = new Set<string>(); (list || []).forEach((id) => { if (live.has(id)) s.add(id); }); return s; };
    let all: string[] = []; if (data.v === 2) { (data.groups || []).forEach((g) => { all = all.concat(g.m || []); }); all = all.concat(data.manual || []); }
    const byId = data.v === 2 && (!all.length || all.some((id) => live.has(id)));
    const pick = (ids?: string[], pos?: Pos[]): Set<string> => (data.v === 1 ? fromPos(ids as unknown as Pos[]) : byId ? fromIds(ids) : fromPos(pos));
    st.groups = (data.groups || []).map((g) => ({
      id: g.id !== undefined && g.id !== null && data.v === 2 ? g.id : newId(), name: g.name || 'Gruppe', hidden: !!g.hidden, sticky: !!g.sticky,
      members: data.v === 1 ? fromPos(g.m as unknown as Pos[]) : pick(g.m, g.p)
    })).filter((g) => g.members.size);
    st.manual = data.v === 1 ? fromPos(data.manual as unknown as Pos[]) : pick(data.manual, data.manualP);
    (data.auto || []).forEach((k) => st.auto.add(k));
    return st;
  }
  const MARK = '// gsme-groups ';
  /** Zeile für das Ende der .map-Datei (Kommentar, vom Compiler ignoriert) oder '' */
  function toComment(doc: Doc, st: VisState): string {
    const d = serialize(doc, st);
    if (!d.groups.length && !d.manual.length && !d.auto.length) return '';
    return MARK + JSON.stringify(d) + '\n';
  }
  function fromText(text: string): SavedGroups | null {
    const i = text.lastIndexOf(MARK); if (i < 0) return null;
    const j = text.indexOf('\n', i); try { return JSON.parse(text.slice(i + MARK.length, j < 0 ? undefined : j)); } catch (e) { return null; }
  }
  return { AUTO, autoKey, hiddenIds, filterDoc, autoCounts, liveIds, selIds, expand, splitIds, serialize, restore, toComment, fromText, isPoint };
})(Core, EntDefs);
export default Groups;
